import React from 'react';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { preprocessTextForWrapping, splitText, estimateTextWidth, getSiglaturaPerStampa } from '../../utils/cableLogic';
import CableSVGLine from './CableSVGLine';
import { COLOR_MAP } from '../../constants/colorConstants';
import { SECTION_SCALE_MAP, BASE_TERMINAL_SCALE, MIN_TERMINAL_SCALE, MAX_TERMINAL_SCALE, TERMINAL_SCALE_PROPORTIONAL_FACTOR } from '../../constants/terminalConstants';

// Funzione per verificare se è un cavo di terra
const isEarthWire = (colore) => {
    return colore === 'GI/VE' || colore === 'YE/GN';
};


// Componente per visualizzazione cavi multipolari
const MultipolarCableVisualization = ({ cable, hoveredCable, setHoveredCable, isMobile, settings }) => {
    // ✅ Controllo per struttura multipolare separata
    if (!cable.poli || !Array.isArray(cable.poli)) {
        return null; // Non è un cavo multipolare
    }

    const svgWidth = isMobile ? 400 : 800;
    const svgHeight = Math.max(200, 80 + (cable.poli.length * 60)); // Altezza dinamica
    const mainCableY = svgHeight / 2;

    // Dimensioni cavo principale (rettangolo centrale)
    const cableWidth = 300;
    const cableHeight = 40;
    const cableX = (svgWidth - cableWidth) / 2;
    const cableRect = {
        x: cableX,
        y: mainCableY - cableHeight / 2,
        width: cableWidth,
        height: cableHeight
    };

    // Spazio per i poli a sinistra e destra
    const poloLeftX = 120;
    const poloRightX = svgWidth - 120;
    const poloSpacing = Math.min(50, (svgHeight - 100) / Math.max(1, cable.poli.length - 1));
    const poloStartY = svgHeight / 2 - ((cable.poli.length - 1) * poloSpacing) / 2;

    return (
        <svg
            className="w-full"
            viewBox={`0 0 ${svgWidth} ${svgHeight} `}
            style={{ backgroundColor: '#f8f9fa' }}
            onMouseLeave={() => setHoveredCable(null)}
        >


            {/* Rendering Poli */}
            {(() => {
                // Raggruppa le lunghezze uniche per lato A e B
                const lunghezzeUnicheA = [...new Set(cable.poli.map(p => p.lunghezzaA || 0))].filter(l => l > 0).sort((a, b) => a - b);
                const lunghezzeUnicheB = [...new Set(cable.poli.map(p => p.lunghezzaB || 0))].filter(l => l > 0).sort((a, b) => a - b);

                // Calcola la lunghezza massima per la scala proporzionale
                const maxLunghezzaA = Math.max(...lunghezzeUnicheA);
                const maxLunghezzaB = Math.max(...lunghezzeUnicheB);
                const maxScale = 80; // Lunghezza massima visiva in pixel

                return (
                    <>
                        {/* RENDERING POLI */}
                        {cable.poli.map((polo, index) => {
                            const poloY = poloStartY + (index * poloSpacing);
                            const isHovered = hoveredCable === cable.originalIndex;

                            // ✅ CALCOLI CORRETTI PER LATO A (sinistra)
                            const cableExitXA = cableRect.x;
                            const cableExitYA = cableRect.y + (cableRect.height / (cable.poli.length + 1)) * (index + 1);

                            // Lunghezza proporzionale alla spelatura A - SOLO per tratto orizzontale
                            const lunghezzaProporzioneA = (polo.lunghezzaA || 0) / Math.max(maxLunghezzaA, 1);
                            const lunghezzaVisualeA = lunghezzaProporzioneA * maxScale;

                            // ✅ CORREZIONE: Il tratto obliquo rimane fisso, solo quello orizzontale cambia
                            const horizontalStartXA = poloLeftX + 40; // Posizione fissa per inizio tratto orizzontale
                            const horizontalEndXA = horizontalStartXA - lunghezzaVisualeA; // Fine proporzionale

                            // ✅ CALCOLI CORRETTI PER LATO B (destra)
                            const cableExitXB = cableRect.x + cableRect.width;
                            const cableExitYB = cableRect.y + (cableRect.height / (cable.poli.length + 1)) * (index + 1);

                            // Lunghezza proporzionale alla spelatura B - SOLO per tratto orizzontale
                            const lunghezzaProporzioneB = (polo.lunghezzaB || 0) / Math.max(maxLunghezzaB, 1);
                            const lunghezzaVisualeB = lunghezzaProporzioneB * maxScale;

                            // ✅ CORREZIONE: Il tratto obliquo rimane fisso, solo quello orizzontale cambia
                            const horizontalStartXB = poloRightX - 40; // Posizione fissa per inizio tratto orizzontale
                            const horizontalEndXB = horizontalStartXB + lunghezzaVisualeB; // Fine proporzionale

                            return (
                                <g key={polo.id}>
                                    {/* LATO A (SINISTRA) */}
                                    <g
                                        onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                                        onMouseLeave={() => setHoveredCable(null)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {/* ✅ Linea obliqua dal cavo verso il punto fisso */}
                                        <CableSVGLine
                                            cable={polo}
                                            x1={cableExitXA}
                                            y1={cableExitYA}
                                            x2={horizontalStartXA}
                                            y2={poloY}
                                            isHovered={isHovered}
                                            useAWG={settings.useAWG}
                                            strokeLinecap="round"
                                            opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                        />

                                        {/* ✅ Tratto orizzontale proporzionale */}
                                        <CableSVGLine
                                            cable={polo}
                                            x1={horizontalStartXA}
                                            y1={poloY}
                                            x2={horizontalEndXA}
                                            y2={poloY}
                                            isHovered={isHovered}
                                            useAWG={settings.useAWG}
                                            strokeLinecap="round"
                                            opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                        />

                                        {/* ✅ Terminale A alla fine del tratto orizzontale */}
                                        {renderTerminalSchematic(
                                            polo.terminaleA,
                                            horizontalEndXA,
                                            poloY,
                                            isMobile ? 0.03 : 0.04,
                                            'A',
                                            180
                                        )}

                                        {/* Codice terminale A */}
                                        {polo.terminaleA && polo.terminaleA !== 'NS' && (
                                            <text
                                                x={horizontalEndXA}
                                                y={poloY + 20}
                                                className="text-xs font-medium fill-gray-700"
                                                textAnchor="middle"
                                            >
                                                {polo.terminaleA}
                                            </text>
                                        )}

                                        {/* Etichetta siglatura A */}
                                        {polo.siglaturaA && (
                                            <g>
                                                <rect
                                                    x={horizontalEndXA + 40 - 6}
                                                    y={poloY - 8}
                                                    width={(polo.siglaturaA.length * 8) + 12}
                                                    height={16}
                                                    rx={3}
                                                    fill="white"
                                                    stroke="#D1D5DB"
                                                    strokeWidth={1}
                                                    filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                                                />
                                                <text
                                                    x={horizontalEndXA + 40}
                                                    y={poloY + 4}
                                                    className="text-xs font-bold fill-gray-800"
                                                    textAnchor="start"
                                                >
                                                    {polo.siglaturaA}
                                                </text>
                                            </g>
                                        )}

                                        {/* Collegamento A */}
                                        {polo.collegamentoA && (
                                            <text
                                                x={horizontalEndXA - 30}
                                                y={poloY + 3}
                                                className="text-xs font-medium"
                                                textAnchor="end"
                                                fontSize="10"
                                                fill="#374151"
                                            >
                                                {splitText(preprocessTextForWrapping(polo.collegamentoA), 8).map((line, i) => (
                                                    <tspan
                                                        key={i}
                                                        x={horizontalEndXA - 35}
                                                        dy={i === 0 ? 0 : 12}  // Spaziatura tra le righe
                                                    >
                                                        {line}
                                                    </tspan>
                                                ))}
                                            </text>
                                        )}
                                    </g>

                                    {/* LATO B (DESTRA) */}
                                    <g
                                        onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                                        onMouseLeave={() => setHoveredCable(null)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {/* ✅ Linea obliqua dal cavo verso il punto fisso */}
                                        <CableSVGLine
                                            cable={polo}
                                            x1={cableExitXB}
                                            y1={cableExitYB}
                                            x2={horizontalStartXB}
                                            y2={poloY}
                                            isHovered={isHovered}
                                            useAWG={settings.useAWG}
                                            strokeLinecap="round"
                                            opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                        />
                                        {/* ✅ Tratto orizzontale proporzionale */}
                                        <CableSVGLine
                                            cable={polo}
                                            x1={horizontalStartXB}
                                            y1={poloY}
                                            x2={horizontalEndXB}
                                            y2={poloY}
                                            isHovered={isHovered}
                                            useAWG={settings.useAWG}
                                            strokeLinecap="round"
                                            opacity={isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1)}
                                        />

                                        {/* ✅ Terminale B alla fine del tratto orizzontale */}
                                        {renderTerminalSchematic(
                                            polo.terminaleB,
                                            horizontalEndXB,
                                            poloY,
                                            isMobile ? 0.03 : 0.04,
                                            'B',
                                            0
                                        )}

                                        {/* Codice terminale B */}
                                        {polo.terminaleB && polo.terminaleB !== 'NS' && (
                                            <text
                                                x={horizontalEndXB}
                                                y={poloY + 20}
                                                className="text-xs font-medium fill-gray-700"
                                                textAnchor="middle"
                                            >
                                                {polo.terminaleB}
                                            </text>
                                        )}

                                        {/* Etichetta siglatura B */}
                                        {polo.siglaturaB && (
                                            <g>
                                                <rect
                                                    x={horizontalEndXB - 40 - (polo.siglaturaB.length * 8) - 6}
                                                    y={poloY - 8}
                                                    width={(polo.siglaturaB.length * 8) + 12}
                                                    height={16}
                                                    rx={3}
                                                    fill="white"
                                                    stroke="#D1D5DB"
                                                    strokeWidth={1}
                                                    filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
                                                />
                                                <text
                                                    x={horizontalEndXB - 40}
                                                    y={poloY + 4}
                                                    className="text-xs font-bold fill-gray-800"
                                                    textAnchor="end"
                                                >
                                                    {polo.siglaturaB}
                                                </text>
                                            </g>
                                        )}

                                        {/* Collegamento B */}
                                        {polo.collegamentoB && (
                                            <text
                                                x={horizontalEndXB + 30}
                                                y={poloY + 3}
                                                className="text-xs font-medium"
                                                textAnchor="start"
                                                fontSize="10"
                                                fill="#374151"
                                            >
                                                {splitText(preprocessTextForWrapping(polo.collegamentoB), 8).map((line, i) => (
                                                    <tspan
                                                        key={i}
                                                        x={horizontalEndXB + 35}
                                                        dy={i === 0 ? 0 : 12}  // Spaziatura tra le righe
                                                    >
                                                        {line}
                                                    </tspan>
                                                ))}
                                            </text>
                                        )}
                                    </g>
                                </g>
                            );
                        })}

                        {/* ✅ QUOTE CORRETTE LATO A */}
                        {lunghezzeUnicheA.filter(lunghezza => lunghezza > 0).map((lunghezza, index) => {
                            // Trova il primo polo con questa lunghezza per posizionare la quota
                            const primoPoloConQuesta = cable.poli.find(p => (p.lunghezzaA || 0) === lunghezza);
                            const primoPoloIndex = cable.poli.indexOf(primoPoloConQuesta);
                            const quotaY = poloStartY + (primoPoloIndex * poloSpacing);

                            // Calcola posizioni per questa lunghezza specifica
                            const cableExitXA = cableRect.x;
                            const lunghezzaProporzione = lunghezza / maxLunghezzaA;
                            const lunghezzaVisuale = lunghezzaProporzione * maxScale;
                            const horizontalStartXA = poloLeftX + 40;
                            const horizontalEndXA = horizontalStartXA - lunghezzaVisuale;

                            // ✅ ALTEZZA QUOTE: tutte alla stessa altezza della linea orizzontale
                            const quotaLineY = quotaY - 18 - (index * 12);

                            return (
                                <g key={`quota - a - ${lunghezza} `} className="opacity-70">
                                    {/* ✅ Linee di quota verticali - CORRETTE */}
                                    <line
                                        x1={cableExitXA}
                                        y1={quotaLineY - 5}
                                        x2={cableExitXA}
                                        y2={quotaLineY + 5}
                                        stroke="#666"
                                        strokeWidth="0.5"
                                    />
                                    <line
                                        x1={horizontalEndXA}
                                        y1={quotaLineY - 5}
                                        x2={horizontalEndXA}
                                        y2={quotaLineY + 5}
                                        stroke="#666"
                                        strokeWidth="0.5"
                                    />

                                    {/* Linea di quota orizzontale */}
                                    <line
                                        x1={cableExitXA}
                                        y1={quotaLineY}
                                        x2={horizontalEndXA}
                                        y2={quotaLineY}
                                        stroke="#666"
                                        strokeWidth="0.5"
                                    />

                                    {/* Testo quota */}
                                    <text
                                        x={(cableExitXA + horizontalEndXA) / 2}
                                        y={quotaLineY - 4}
                                        className="text-xs fill-gray-600 font-medium"
                                        textAnchor="middle"
                                        fontSize="9"
                                    >
                                        {lunghezza}mm
                                    </text>
                                </g>
                            );
                        })}
                        {/* ✅ QUOTE CORRETTE LATO B */}
                        {lunghezzeUnicheB.filter(lunghezza => lunghezza > 0).map((lunghezza, index) => {
                            // Trova il primo polo con questa lunghezza per posizionare la quota
                            const primoPoloConQuesta = cable.poli.find(p => (p.lunghezzaB || 0) === lunghezza);
                            const primoPoloIndex = cable.poli.indexOf(primoPoloConQuesta);
                            const quotaY = poloStartY + (primoPoloIndex * poloSpacing);

                            // Calcola posizioni per questa lunghezza specifica
                            const cableExitXB = cableRect.x + cableRect.width;
                            const lunghezzaProporzione = lunghezza / maxLunghezzaB;
                            const lunghezzaVisuale = lunghezzaProporzione * maxScale;
                            const horizontalStartXB = poloRightX - 40;
                            const horizontalEndXB = horizontalStartXB + lunghezzaVisuale;

                            // ✅ ALTEZZA QUOTE: tutte alla stessa altezza della linea orizzontale
                            const quotaLineY = quotaY - 18 - (index * 12);

                            return (
                                <g key={`quota - b - ${lunghezza} `} className="opacity-70">
                                    {/* ✅ Linee di quota verticali - CORRETTE */}
                                    <line
                                        x1={cableExitXB}
                                        y1={quotaLineY - 5}
                                        x2={cableExitXB}
                                        y2={quotaLineY + 5}
                                        stroke="#666"
                                        strokeWidth="0.5"
                                    />
                                    <line
                                        x1={horizontalEndXB}
                                        y1={quotaLineY - 5}
                                        x2={horizontalEndXB}
                                        y2={quotaLineY + 5}
                                        stroke="#666"
                                        strokeWidth="0.5"
                                    />

                                    {/* Linea di quota orizzontale */}
                                    <line
                                        x1={cableExitXB}
                                        y1={quotaLineY}
                                        x2={horizontalEndXB}
                                        y2={quotaLineY}
                                        stroke="#666"
                                        strokeWidth="0.5"
                                    />

                                    {/* Testo quota */}
                                    <text
                                        x={(cableExitXB + horizontalEndXB) / 2}
                                        y={quotaLineY - 4}
                                        className="text-xs fill-gray-600 font-medium"
                                        textAnchor="middle"
                                        fontSize="9"
                                    >
                                        {lunghezza}mm
                                    </text>
                                </g>
                            );
                        })}
                    </>
                );
            })()}

            {/* Cavo principale - rettangolo */}
            <rect
                x={cableRect.x}
                y={cableRect.y}
                width={cableRect.width}
                height={cableRect.height}
                fill="#e5e7eb"
                stroke="#374151"
                strokeWidth="2"
                rx="8"
            />

            {/* Numero del cavo */}
            <g>
                <circle
                    cx={cableX + cableWidth / 2}
                    cy={cableRect.y - 25}
                    r={15}
                    fill="#1F2937"
                    opacity={0.9}
                />
                <text
                    className="cable-number"
                    x={cableX + cableWidth / 2}
                    y={cableRect.y - 20}
                    fontSize={14}
                    fill="white"
                    textAnchor="middle"
                >
                    #{cable.originalIndex + 1}
                </text>
            </g>

            {/* Nome cavo e lunghezza */}
            <text
                x={cableX + cableWidth / 2}
                y={cableRect.y + cableHeight + 20}
                className="text-sm fill-gray-800"
                textAnchor="middle"
                fontSize="12"
            >
                {cable.nomeCavo} • {cable.lunghezza}mm
            </text>

            {/* Indicazione A (lato sinistro) */}
            <g>
                <rect
                    x={cableRect.x + 10}
                    y={cableRect.y + cableHeight / 2 - 8}
                    width={20}
                    height={16}
                    fill="#dcfce7"
                    stroke="#16a34a"
                    strokeWidth="1"
                    rx="2"
                />
                <text
                    x={cableRect.x + 20}
                    y={cableRect.y + cableHeight / 2 + 4}
                    className="text-xs font-bold"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#15803d"
                >
                    A
                </text>
            </g>

            {/* Badge numero poli */}
            <rect
                x={cableX + cableWidth / 2 - 30}
                y={cableRect.y + cableHeight / 2 - 8}
                width={60}
                height={16}
                fill="#8b5cf6"
                rx="8"
            />
            <text
                x={cableX + cableWidth / 2}
                y={cableRect.y + cableHeight / 2 + 4}
                className="text-xs font-bold fill-white"
                textAnchor="middle"
                fontSize="10"
            >
                {cable.poli.length}G{cable.poli[0]?.sezione || 0.5}
            </text>

            {/* Indicazione B (lato destro) */}
            <g>
                <rect
                    x={cableRect.x + cableWidth - 30}
                    y={cableRect.y + cableHeight / 2 - 8}
                    width={20}
                    height={16}
                    fill="#fecaca"
                    stroke="#dc2626"
                    strokeWidth="1"
                    rx="2"
                />
                <text
                    x={cableRect.x + cableWidth - 20}
                    y={cableRect.y + cableHeight / 2 + 4}
                    className="text-xs font-bold"
                    textAnchor="middle"
                    fontSize="10"
                    fill="#dc2626"
                >
                    B
                </text>
            </g>
        </svg>
    );
};

export default MultipolarCableVisualization;